/**
 * Checkout Modal Component
 * 
 * Handles payment collection using Stripe Elements.
 * Supports both one-time payments and subscriptions.
 */

import React, { useState, useEffect } from 'react';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { X, Loader2, CheckCircle, AlertCircle, CreditCard, Smartphone } from 'lucide-react';
import { Product, ProductType, formatPrice, formatBillingInterval } from '../../types/payments';
import { useCreatePaymentIntent, useCreateSubscription } from '../../hooks/usePayments';
import { useThemeStore } from '../../stores/themeStore';

// Initialize Stripe (will be set when we get the publishable key)
let stripePromise: Promise<any> | null = null;

interface CheckoutFormProps {
  product: Product;
  onSuccess: () => void;
  onError: (message: string) => void;
}

const CheckoutForm: React.FC<CheckoutFormProps> = ({ product, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const theme = useThemeStore((state) => state.theme);
  const isDark = theme === 'dark';
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }
    
    setIsProcessing(true);
    setMessage(null);
    
    try {
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment-success`,
        },
        redirect: 'if_required',
      });
      
      if (error) {
        setMessage(error.message || 'An error occurred during payment.');
        onError(error.message || 'Payment failed');
      } else {
        // Payment succeeded
        onSuccess();
      }
    } catch (err: any) {
      setMessage(err.message || 'An unexpected error occurred.');
      onError(err.message || 'Payment failed');
    } finally {
      setIsProcessing(false);
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <PaymentElement 
        options={{
          layout: 'tabs',
        }}
      />
      
      {message && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-500">{message}</p>
        </div>
      )}
      
      <button
        type="submit"
        disabled={isProcessing || !stripe || !elements}
        className={`
          w-full py-4 px-6 rounded-xl font-semibold text-white transition-all duration-200
          bg-gradient-to-r from-cyan-500 to-teal-500
          hover:shadow-lg hover:shadow-cyan-500/30
          disabled:opacity-50 disabled:cursor-not-allowed
          flex items-center justify-center gap-2
        `}
      >
        {isProcessing ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Processing...
          </>
        ) : (
          <>
            <CreditCard className="w-5 h-5" />
            Pay {formatPrice(product.price_amount, product.currency)}
          </>
        )}
      </button>
      
      <p className={`text-xs text-center ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
        By completing this purchase, you agree to our Terms of Service and Privacy Policy.
      </p>
    </form>
  );
};

interface CheckoutModalProps {
  product: Product;
  onClose: () => void;
  onSuccess: () => void;
}

export const CheckoutModal: React.FC<CheckoutModalProps> = ({ product, onClose, onSuccess }) => {
  const theme = useThemeStore((state) => state.theme);
  const isDark = theme === 'dark';
  
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [publishableKey, setPublishableKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  
  const createPaymentIntent = useCreatePaymentIntent();
  const createSubscription = useCreateSubscription();
  
  const isSubscription = product.product_type === ProductType.SUBSCRIPTION;
  
  // Create payment intent or subscription on mount
  // Use isInitializing flag to prevent duplicate calls from React Strict Mode
  useEffect(() => {
    let isCancelled = false;
    
    const initPayment = async () => {
      // Prevent duplicate initialization
      if (isInitializing || clientSecret) return;
      setIsInitializing(true);
      
      try {
        if (isSubscription) {
          const result = await createSubscription.mutateAsync({
            product_id: product.id,
          });
          
          if (isCancelled) return;
          
          if (result.client_secret) {
            setClientSecret(result.client_secret);
          } else if (!result.requires_action) {
            // Subscription created without payment needed (e.g., trial)
            setPaymentSuccess(true);
          }
          setPublishableKey(result.publishable_key);
        } else {
          const result = await createPaymentIntent.mutateAsync({
            product_id: product.id,
            quantity: 1,
          });
          
          if (isCancelled) return;
          
          setClientSecret(result.client_secret);
          setPublishableKey(result.publishable_key);
        }
      } catch (err: any) {
        if (!isCancelled) {
          setError(err.response?.data?.detail || err.message || 'Failed to initialize payment');
        }
      } finally {
        if (!isCancelled) {
          setIsInitializing(false);
        }
      }
    };
    
    initPayment();
    
    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.id, isSubscription]);
  
  // Initialize Stripe when we have the publishable key
  useEffect(() => {
    if (publishableKey && !stripePromise) {
      stripePromise = loadStripe(publishableKey);
    }
  }, [publishableKey]);
  
  const handleSuccess = () => {
    setPaymentSuccess(true);
    setTimeout(() => {
      onSuccess();
    }, 2000);
  };
  
  const handleError = (message: string) => {
    setError(message);
  };
  
  const elementsOptions: StripeElementsOptions = {
    clientSecret: clientSecret || undefined,
    appearance: {
      theme: isDark ? 'night' : 'stripe',
      variables: {
        colorPrimary: '#06b6d4',
        borderRadius: '12px',
        fontFamily: 'system-ui, sans-serif',
      },
    },
  };
  
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className={`
        relative w-full max-w-md mx-4 rounded-2xl overflow-hidden
        ${isDark ? 'bg-slate-800' : 'bg-white'}
        shadow-2xl
      `}>
        {/* Header */}
        <div className={`
          p-6 border-b
          ${isDark ? 'border-white/10' : 'border-slate-200'}
        `}>
          <button
            onClick={onClose}
            className={`
              absolute top-4 right-4 p-2 rounded-full transition-colors
              ${isDark ? 'hover:bg-white/10 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}
            `}
          >
            <X className="w-5 h-5" />
          </button>
          
          <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
            Complete Your {isSubscription ? 'Subscription' : 'Purchase'}
          </h2>
          
          {/* Product Summary */}
          <div className={`mt-4 p-4 rounded-xl ${isDark ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
            <div className="flex justify-between items-start">
              <div>
                <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>
                  {product.name}
                </h3>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  {product.description}
                </p>
              </div>
              <div className="text-right">
                <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
                  {formatPrice(product.price_amount, product.currency)}
                </p>
                {isSubscription && (
                  <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    {formatBillingInterval(product.billing_interval)}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6">
          {paymentSuccess ? (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
              <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>
                Payment Successful!
              </h3>
              <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                Thank you for your purchase. Redirecting...
              </p>
            </div>
          ) : error && !clientSecret ? (
            <div className="text-center py-8">
              <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>
                Payment Error
              </h3>
              <p className={`mb-4 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                {error}
              </p>
              <button
                onClick={onClose}
                className="px-6 py-2 rounded-xl bg-slate-100 text-slate-800 font-medium hover:bg-slate-200"
              >
                Try Again
              </button>
            </div>
          ) : !clientSecret ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-10 h-10 text-cyan-500 animate-spin mb-4" />
              <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                Preparing checkout...
              </p>
            </div>
          ) : stripePromise ? (
            <Elements stripe={stripePromise} options={elementsOptions}>
              <CheckoutForm 
                product={product} 
                onSuccess={handleSuccess}
                onError={handleError}
              />
            </Elements>
          ) : (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-10 h-10 text-cyan-500 animate-spin mb-4" />
              <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                Loading payment form...
              </p>
            </div>
          )}
        </div>
        
        {/* Payment Methods */}
        {!paymentSuccess && clientSecret && (
          <div className={`
            px-6 pb-6 pt-0
            border-t ${isDark ? 'border-white/10' : 'border-slate-200'}
          `}>
            <div className="flex items-center justify-center gap-4 pt-4">
              <div className="flex items-center gap-1 text-xs text-slate-400">
                <CreditCard className="w-4 h-4" />
                <span>Card</span>
              </div>
              <div className="flex items-center gap-1 text-xs text-slate-400">
                <Smartphone className="w-4 h-4" />
                <span>Apple Pay</span>
              </div>
              <div className="flex items-center gap-1 text-xs text-slate-400">
                <Smartphone className="w-4 h-4" />
                <span>Google Pay</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckoutModal;

